'use client'

import { ArrowUp } from 'lucide-react'
import { useState } from 'react'
import { Button } from '@/components/ui/button'
import type { ChatAnswerType, ChatStepId } from '@/lib/postgraduate-chat/chat-flow'
import { formatPhoneMask } from '@/lib/postgraduate-chat/phone'

const PLACEHOLDERS: Record<ChatAnswerType, string> = {
  text: 'Escreva sua resposta…',
  email: 'seu@email.com',
  phone: '+55 (XX) XXXXX-XXXX',
  options: 'Escreva sua resposta…',
}

const INPUT_TYPES: Record<ChatAnswerType, string> = {
  text: 'text',
  email: 'email',
  phone: 'tel',
  options: 'text',
}

const PHONE_SEED = '+55'

interface ChatComposerProps {
  readonly stepId: ChatStepId | null
  readonly answerType: ChatAnswerType | null
  readonly disabled: boolean
  readonly onSubmit: (stepId: ChatStepId, value: string) => void
}

export function ChatComposer({ stepId, answerType, disabled, onSubmit }: ChatComposerProps) {
  return (
    <ComposerField
      answerType={answerType}
      disabled={disabled}
      key={`${stepId ?? 'none'}-${answerType ?? 'none'}`}
      onSubmit={onSubmit}
      stepId={stepId}
    />
  )
}

function ComposerField({ stepId, answerType, disabled, onSubmit }: ChatComposerProps) {
  const [value, setValue] = useState(answerType === 'phone' ? PHONE_SEED : '')
  const isInteractive = Boolean(stepId) && Boolean(answerType) && !disabled

  function handleChange(event: React.ChangeEvent<HTMLInputElement>) {
    setValue(
      answerType === 'phone' ? formatPhoneMask(event.target.value) : event.target.value
    )
  }

  function handleSubmit(event: React.FormEvent) {
    event.preventDefault()
    if (!(stepId && isInteractive)) {
      return
    }
    const trimmed = value.trim()
    if (!trimmed) {
      return
    }
    onSubmit(stepId, value)
  }

  const placeholder = answerType ? PLACEHOLDERS[answerType] : 'Escreva sua resposta…'
  const inputType = answerType ? INPUT_TYPES[answerType] : 'text'

  return (
    <form
      className="flex shrink-0 items-center gap-2 border-neutral-200 border-t px-3 py-2.5"
      onSubmit={handleSubmit}
    >
      <label className="sr-only" htmlFor="postgraduate-chat-composer-input">
        Sua resposta
      </label>
      <input
        className="min-w-0 flex-1 rounded-full border border-neutral-200 px-3.5 py-2.5 font-rethink text-neutral-800 text-sm outline-none placeholder:text-neutral-400 focus-visible:border-primary-800 focus-visible:ring-2 focus-visible:ring-primary-800/30 disabled:cursor-not-allowed disabled:bg-neutral-50 disabled:text-neutral-400"
        disabled={!isInteractive}
        id="postgraduate-chat-composer-input"
        onChange={handleChange}
        placeholder={placeholder}
        type={inputType}
        value={value}
      />
      <Button
        aria-label="Enviar resposta"
        className="!w-10 h-10 shrink-0 rounded-full p-0"
        disabled={!isInteractive}
        size="icon"
        type="submit"
      >
        <ArrowUp className="size-4" />
      </Button>
    </form>
  )
}
